import { createTheme, Box, styled, FormControl } from '@mui/material'
import MuiAppBar from '@mui/material/AppBar'
import MuiInput from '@mui/material/Input'

import type { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    small: true
    medium: true
    mediumBold: true
    largeBold: true
  }
}

// フォントサイズ
export const smallFontSize = '0.75rem'
export const mediumFontSize = '1rem'
export const largeFontSize = '1.3rem'

// 入力サイズ
export const inputWidth = '16rem'
export const fileInputWidth = '35rem'

// 商品、仕入先詳細に関するmargin
export const detailCompornentMarginBottom = '1rem'

// Boxコンポーネントの個別スタイル群
export const boxMarginTop = { marginTop: 1 }

export const theme = createTheme({
  spacing: 10,
  typography: {
    fontSize: 12,
    h1: { fontSize: 20 },
    h2: { fontSize: 16, fontWeight: 600 },
    h3: { fontSize: 12 },
  },
  breakpoints: {
    values: {
      xs: 3,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1900,
    },
  },
  palette: {
    primary: {
      main: '#bdbdbd',
    },
    secondary: {
      main: '#eeeeee',
    },
  },
  components: {
    MuiToolbar: {
      styleOverrides: {
        root: {
          '@media (min-width: 600px)': {
            minHeight: '3rem',
          },
        },
      },
    },
    MuiAccordion: {
      defaultProps: {
        sx: {
          marginBottom: detailCompornentMarginBottom,
        },
      },
    },
    MuiCollapse: {
      defaultProps: {
        sx: {
          marginBottom: detailCompornentMarginBottom,
        },
      },
    },
    MuiContainer: {
      defaultProps: {
        sx: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    },
    MuiTypography: {
      // 追加variants
      // フォントサイズが３段階で構成されその中で太字が追加される
      variants: [
        {
          props: { variant: 'small' },
          style: {
            fontSize: smallFontSize,
          },
        },
        {
          props: { variant: 'medium' },
          style: {
            fontSize: mediumFontSize,
          },
        },
        {
          props: { variant: 'mediumBold' },
          style: {
            fontSize: mediumFontSize,
            fontWeight: 'bold',
          },
        },
        {
          props: { variant: 'largeBold' },
          style: {
            fontSize: largeFontSize,
            fontWeight: 'bold',
          },
        },
      ],
      styleOverrides: {
        root: {
          marginBottom: 4,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          fontSize: mediumFontSize,
          fontWeight: 'bold',
          height: '1.8rem',
          marginRight: '0.7rem',
          padding: '8px 10px',
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          fontSize: mediumFontSize,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
        sx: {
          width: inputWidth,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        // 商品・仕入先詳細などでInputLabelとInputを用いた際に間のmarginを無くす
        root: {
          '& +.MuiInputBase-root': {
            marginTop: 0,
          },
        },
        formControl: {
          // 移動をクリック時に動かないように固定
          position: 'static',
          transform: 'none',
          transition: 'none',
          // クリックを可能に
          pointerEvents: 'auto',
          cursor: 'pointer',
          // 幅いっぱいを解除
          display: 'inline',
          alignSelf: 'start',
          // タイポグラフィを指定
          fontWeight: 'bold',
          fontSize: smallFontSize,
          // ラベル
          color: '#424242',
          marginTop: '0.5rem',
        },
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          marginTop: 0,
          backgroundColor: 'white',
          // テキストの高さを固定
          height: '2.3rem !important',
          fontSize: mediumFontSize,
        },
        notchedOutline: {
          top: 0,
          legend: {
            display: 'none',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          // MEMO: リスト項目の高さを指定しても別のクラスでオーバライドされているため以下の記述で、高さを再割り当てする
          // 参考: https://github.com/mui/material-ui/issues/29703#issuecomment-996586877
          '@media (min-width: 600px)': {
            minHeight: '2.3rem',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          paddingTop: '6px',
          paddingBottom: '6px',
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
        sx: {
          height: 'auto',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: {
          backgroundColor: '#e3e3e3',
          marginBottom: '0.4rem',
          paddingLeft: '0.5rem',
          paddingBottom: '0.25rem',
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        sx: {
          paddingRight: '0.3rem',
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        sx: {
          width: inputWidth,
          display: 'inline-flex',
        },
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            height: 'auto !important',
            minHeight: '2.3rem',
            paddingTop: '2px',
            paddingBottom: '2px',
          },
          '& .MuiTextField-root': {
            width: '100%',
          },
          '& .MuiChip-root': {
            height: '20px',
            fontSize: '0.75rem',
          },
          '& .MuiChip-deleteIcon': {
            fontSize: '14px',
          },
        },
      },
    },
    MuiAlert: {
      defaultProps: {
        sx: {
          lineHeight: 2,
          padding: '2px 13px',
        },
      },
      styleOverrides: {
        root: {
          '& .MuiAlert-icon': {
            marginRight: 0,
          },
        },
      },
    },
    // タブコンポーネント選択時の色変更
    MuiTab: {
      defaultProps: {
        sx: {
          fontWeight: 'bold',
        },
      },
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#e3e3e3',
            color: 'black',
          },
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: {
          color: 'black',
        },
      },
    },
    MuiTabs: {
      // タブをラウンド状にする
      styleOverrides: {
        root: {
          minHeight: 0,
          '& .MuiTab-root': {
            borderRadius: '8px 8px 0 0',
            maxHeight: '30px',
            minHeight: 0,
            fontSize: mediumFontSize,
            fontWeight: 'bold',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '2.1rem',
          color: 'white',
        },
      },
    },
    MuiSvgIcon: {
      defaultProps: {
        sx: {
          fontSize: 24,
        },
      },
    },
    MuiGrid: {
      defaultProps: {
        sx: {
          marginTop: 'auto',
        },
      },
    },
  },
})

// サイドバー用のコンポーネント
interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

export const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme: trTheme, open }) => ({
  transition: trTheme.transitions.create(['margin', 'width'], {
    easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
    duration: 0,
  }),
  ...(open && {
    width: `calc(100% - 210px)`,
    marginLeft: `210px`,
    transition: trTheme.transitions.create(['margin', 'width'], {
      easing: trTheme.transitions.easing.easeOut,
      duration: trTheme.transitions.duration.enteringScreen,
    }),
  }),
}))

// SidebarとHeaderコンポーネントのHeader
export const DrawerHeader = styled('div', { shouldForwardProp: (prop) => prop !== 'sidebar' })<{
  sidebar?: boolean
}>(({ theme: trTheme, sidebar }) => ({
  marginBottom: '4px',
  display: 'flex',
  alignItems: 'center',
  padding: sidebar ? trTheme.spacing(0, 1) : 20,
  justifyContent: 'flex-end',
}))

export const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<AppBarProps>(
  ({ theme: trTheme, open }) => ({
    width: '100%',
    flexGrow: 1,
    padding: `${trTheme.spacing(1)} ${trTheme.spacing(1)} 0 ${trTheme.spacing(1)}`,
    transition: trTheme.transitions.create('margin', {
      easing: trTheme.transitions.easing.sharp,
      duration: trTheme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-210px`,
    ...(open && {
      width: `calc(100% - 210px)`,
      padding: `${trTheme.spacing(1)} ${trTheme.spacing(1)} 0 ${trTheme.spacing(1)}`,
      transition: trTheme.transitions.create('margin', {
        easing: trTheme.transitions.easing.easeOut,
        duration: trTheme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  })
)

// 商品詳細・仕入先詳細で主に使用
export const CustomDetailInput = styled(MuiInput, {
  shouldForwardProp: (prop) => prop !== 'doubleWidth',
})<{ doubleWidth?: boolean }>(({ doubleWidth }) => ({
  ...(doubleWidth && {
    width: '18rem',
  }),
}))

// https://mui.com/material-ui/react-button/#file-upload
export const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
})

// Styled form với flex row wrap
export const FlexRowWrapForm = styled('form')({
  display: 'flex',
  flexWrap: 'wrap',
});

// Styled Box: flex row, align end, justify center, margin bottom 5px
export const FlexRowAlignEndJustifyCenterBox = styled(Box)({
  display: 'flex',
  alignItems: 'end',
  justifyContent: 'center',
  marginBottom: '5px',
  height: '55px'
});

// Styled Box: flex row, gap 8px
export const FlexRowGap1Box = styled(Box)({
  display: 'flex',
  gap: 8, // 8px = theme.spacing(1)
});

// Styled Box: compact alert message
export const CompactAlertBox = styled(Box)({
  '& .MuiAlert-root': {
    padding: '2px 5px',
    margin: 0,
    height: '29px',
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
    '& .MuiAlert-icon': {
      paddingLeft: '5px',
    },
    '& .MuiAlert-message': {
      paddingLeft: '5px',
    },

  }
});

// Shared styled components for SelectCenter and SelectSupplier
export const SelectFormControl = styled(FormControl, {
  shouldForwardProp: (prop) => prop !== 'isMultiple',
})<{ isMultiple?: boolean }>(({ isMultiple }) => ({
  width: isMultiple ? '550px' : 'auto',
}))

export const SelectTagsBox = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 0.5,
  width: '550px',
  overflowY: 'auto',
  maxHeight: '30px',
  scrollbarWidth: 'auto',
})
